/**
 * NDT cost-model (frontend mirror of the backend).
 *
 * The per-inspection charge is value-set, not cost-derived: the tier rate sets
 * the price and the reference delivery cost is shown only so the implied margin
 * is visible. This mirror lets the cost-input panel show the rate, reference
 * cost, implied margin, and module total live as a rep edits inputs, without a
 * round-trip. The numbers match backend/app/services/pricing/algorithms/ndt.py;
 * the backend remains the source of truth on Calculate. Vendor onboarding reuses
 * the shared NRE cost-model from materialCostModel.
 */

export const TIER_STANDARD = 'standard';
export const TIER_AI = 'ndt_ai';

export type NDTTier = typeof TIER_STANDARD | typeof TIER_AI;

export interface NDTInputs {
  standardRate: number;
  aiRate: number;
  referenceCostPerInspection: number;
  aiReferenceCost: number;
}

/** Global defaults, mirroring the backend ndt_config locked defaults. */
export const NDT_COST_DEFAULTS: NDTInputs = {
  standardRate: 5,
  aiRate: 10,
  referenceCostPerInspection: 0.35,
  aiReferenceCost: 0.4,
};

export interface ValueLine {
  rate: number;
  referenceCost: number;
  impliedMargin: number;
}

export interface NDTResult {
  base: ValueLine;
  ai?: ValueLine;
  ratePerInspection: number;
}

const valueLine = (rate: number, referenceCost: number): ValueLine => ({
  rate,
  referenceCost,
  impliedMargin: rate > 0 ? 1 - referenceCost / rate : 0,
});

/** Derive the per-inspection value lines for the given tier. */
export const perInspectionPrice = (
  inputs: NDTInputs,
  tier: NDTTier = TIER_STANDARD
): NDTResult => {
  const base = valueLine(inputs.standardRate, inputs.referenceCostPerInspection);
  if (tier !== TIER_AI) {
    return { base, ratePerInspection: base.rate };
  }
  const increment = inputs.aiRate - inputs.standardRate;
  const ai = valueLine(increment, inputs.aiReferenceCost);
  return { base, ai, ratePerInspection: inputs.aiRate };
};
